import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { ADMIN_BASE } from '../../config/adminLayers';

const E = ADMIN_BASE;

const shortcutCards = [
  {
    title: 'Directory & tenants',
    description: 'Users and workspaces, including 360° tenant overview.',
    links: [
      { label: 'Users', to: `${E}/users` },
      { label: 'Workspaces', to: `${E}/workspaces` },
    ],
  },
  {
    title: 'Revenue',
    description: 'Invoices, TDS follow-up, subscriptions, and pricing.',
    links: [
      { label: 'Invoices', to: `${E}/invoices` },
      { label: 'TDS / 26AS', to: `${E}/tds-followup` },
      { label: 'Subscriptions', to: `${E}/subscriptions` },
      { label: 'Pricing', to: `${E}/pricing` },
    ],
  },
  {
    title: 'Email & domains',
    description: 'Cross-tenant verified sending domains.',
    links: [{ label: 'Sending domains', to: `${E}/verified-domains` }],
  },
  {
    title: 'Platform',
    description: 'Operations, voice, credentials, and workflows.',
    links: [
      { label: 'Platform Operations', to: `${E}/platform-operations` },
      { label: 'Operations Center', to: `${E}/operations` },
      { label: 'Voice Infrastructure', to: `${E}/voice-infrastructure` },
      { label: 'API keys', to: `${E}/api-keys` },
    ],
  },
  {
    title: 'Reliability & compliance',
    description: 'Health monitors and admin audit log.',
    links: [
      { label: 'System', to: `${E}/system` },
      { label: 'Monitoring', to: `${E}/monitoring` },
      { label: 'Audit log', to: `${E}/audit` },
    ],
  },
];

export const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dashboardData, setDashboardData] = useState<any>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fastapiService.getDashboardData();
        setDashboardData(response.data || null);
      } catch (e: any) {
        setError(e?.message || 'Failed to load dashboard');
        console.error('Dashboard error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Dashboard</h1>
        <p className="mt-1 text-sm text-admin-600">
          Overview of your SWS CRM system. Use ⌘K / Ctrl+K to jump to any admin page.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="admin-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-admin-500 truncate">Total Users</dt>
                <dd className="text-lg font-medium text-admin-900">{loading ? '—' : dashboardData?.users?.total || 0}</dd>
                <dd className="text-xs text-admin-500 mt-1">{loading ? '' : `${dashboardData?.users?.active || 0} active`}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="admin-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-admin-500 truncate">Workspaces</dt>
                <dd className="text-lg font-medium text-admin-900">{loading ? '—' : dashboardData?.workspaces?.total || 0}</dd>
                <dd className="text-xs text-admin-500 mt-1">{loading ? '' : `${dashboardData?.workspaces?.active || 0} active`}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="admin-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-admin-500 truncate">Workflows</dt>
                <dd className="text-lg font-medium text-admin-900">{loading ? '—' : dashboardData?.workflows?.total || 0}</dd>
                <dd className="text-xs text-admin-500 mt-1">{loading ? '' : `${dashboardData?.workflows?.executionsToday || 0} today`}</dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="admin-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-admin-500 truncate">Total Revenue</dt>
                <dd className="text-lg font-medium text-admin-900">{loading ? '—' : formatCurrency(dashboardData?.invoices?.totalRevenue || 0)}</dd>
                <dd className="text-xs text-admin-500 mt-1">{loading ? '' : `${dashboardData?.invoices?.paid || 0} paid invoices`}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Additional stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="admin-card p-6">
          <div className="text-sm font-medium text-admin-500">New Users This Month</div>
          <div className="mt-2 text-2xl font-bold text-admin-900">{loading ? '—' : dashboardData?.users?.newThisMonth || 0}</div>
        </div>
        <div className="admin-card p-6">
          <div className="text-sm font-medium text-admin-500">Revenue This Month</div>
          <div className="mt-2 text-2xl font-bold text-admin-900">{loading ? '—' : formatCurrency(dashboardData?.invoices?.revenueThisMonth || 0)}</div>
        </div>
        <div className="admin-card p-6">
          <div className="text-sm font-medium text-admin-500">Pending Invoices</div>
          <div className="mt-2 text-2xl font-bold text-yellow-600">{loading ? '—' : dashboardData?.invoices?.pending || 0}</div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="admin-card p-6">
          <h3 className="text-lg font-medium text-admin-900 mb-4">User Statistics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-admin-600">Total Users</span>
              <span className="text-sm font-medium text-admin-900">{dashboardData?.users?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-admin-600">Active Users</span>
              <span className="text-sm font-medium text-green-600">{dashboardData?.users?.active || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-admin-600">New This Month</span>
              <span className="text-sm font-medium text-blue-600">{dashboardData?.users?.newThisMonth || 0}</span>
            </div>
          </div>
        </div>

        <div className="admin-card p-6">
          <h3 className="text-lg font-medium text-admin-900 mb-4">Invoice Statistics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-admin-600">Total Invoices</span>
              <span className="text-sm font-medium text-admin-900">{dashboardData?.invoices?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-admin-600">Paid</span>
              <span className="text-sm font-medium text-green-600">{dashboardData?.invoices?.paid || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-admin-600">Pending</span>
              <span className="text-sm font-medium text-yellow-600">{dashboardData?.invoices?.pending || 0}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-medium text-admin-700">Total Revenue</span>
              <span className="text-sm font-bold text-admin-900">{formatCurrency(dashboardData?.invoices?.totalRevenue || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shortcutCards.map((card) => (
          <div
            key={card.title}
            className="rounded-xl border border-enterprise-200 bg-white p-5 shadow-sm"
          >
            <h3 className="text-lg font-semibold text-enterprise-900">{card.title}</h3>
            <p className="mt-1 text-sm text-enterprise-600">{card.description}</p>
            <ul className="mt-4 space-y-2">
              {card.links.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="group inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900"
                  >
                    {l.label}
                    <ArrowRight className="h-4 w-4 opacity-70 transition group-hover:translate-x-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

